import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

const SUPABASE_STORAGE_BASE = 'https://vltfzozjztfaeifjqpcg.supabase.co/storage/v1/object/public/landing-media/';

const StarRow = ({ count = 5, size = 16, color = '#f59e0b' }) =>
<div style={{ display: 'flex', gap: 2 }}>
    {Array.from({ length: 5 })?.map((_, i) =>
  <svg
    key={i}
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill={i < count ? color : '#e5e7eb'}
    xmlns="http://www.w3.org/2000/svg">

        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
  )}
  </div>;


const getShortTitle = (text) => {
  if (!text) return '';
  const words = text?.trim()?.split(/\s+/);
  return words?.slice(0, 8)?.join(' ') + (words?.length > 8 ? '…' : '');
};

const ReviewCard = ({ review, bg = '#fff', textColor = '#0a2540', starColor = '#f59e0b', border = '1px solid #e2e8f0' }) => {
  if (!review) return null;
  const isLight = bg === '#fff';
  return (
    <div
      style={{
        background: bg,
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: 180,
        border: isLight ? '1px solid #e2e8f0' : 'none',
        boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
        flex: 1
      }}>

      <StarRow count={review?.stars ?? 5} size={14} color={starColor} />
      <p
        style={{
          fontSize: isLight ? 16 : 18,
          fontWeight: isLight ? 400 : 600,
          color: isLight ? '#374151' : textColor,
          margin: '12px 0',
          lineHeight: 1.5,
          flex: 1
        }}>

        {review?.quote_text}
      </p>
      <p style={{ fontSize: 12, color: isLight ? '#9ca3af' : 'rgba(255,255,255,0.7)', margin: 0 }}>
        {review?.author_name}{review?.city ? ` · ${review?.city}` : ''}
      </p>
    </div>);

};

export default function RecensioniSection() {
  const [reviews, setReviews] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoError, setVideoError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reviewsIntervalMs, setReviewsIntervalMs] = useState(4000);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, mediaRes, configRes] = await Promise.all([
        supabase?.from('landing_reviews')?.select('*')?.eq('is_active', true)?.order('sort_order', { ascending: true }),
        supabase?.from('landing_media')?.select('*')?.eq('type', 'reviews_video')?.eq('is_default', true)?.limit(1),
        supabase?.from('landing_config')?.select('key, value')?.eq('key', 'reviews_interval_ms')]
        );

        if (reviewsRes?.data) {
          setReviews(reviewsRes?.data);
          const featuredIdx = reviewsRes?.data?.findIndex((r) => r?.is_featured);
          if (featuredIdx >= 0) setActiveIndex(featuredIdx);
        }

        // Fix: check external_url first, then construct from storage_path
        const mediaRow = mediaRes?.data?.[0];
        if (mediaRow) {
          let url = null;
          if (mediaRow?.external_url && mediaRow?.external_url?.trim() !== '') {
            url = mediaRow?.external_url?.trim();
          } else if (mediaRow?.storage_path && mediaRow?.storage_path?.trim() !== '') {
            url = SUPABASE_STORAGE_BASE + mediaRow?.storage_path?.trim();
          } else if (mediaRow?.url && mediaRow?.url?.trim() !== '') {
            // fallback to legacy url field if present
            url = mediaRow?.url?.trim();
          }
          if (url) setVideoUrl(url);
        }

        if (configRes?.data?.[0]?.value) {
          const parsed = parseInt(configRes?.data?.[0]?.value, 10);
          if (!isNaN(parsed) && parsed > 0) setReviewsIntervalMs(parsed);
        }
      } catch (err) {
        console.error('RecensioniSection fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-rotate featured quote
  useEffect(() => {
    if (reviews?.length <= 1) return;

    const startTimer = () => {
      timerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % reviews?.length);
      }, reviewsIntervalMs);
    };

    startTimer();
    return () => clearInterval(timerRef?.current);
  }, [reviews, reviewsIntervalMs]);

  const handleDotClick = (index) => {
    setActiveIndex(index);
    clearInterval(timerRef?.current);
    if (reviews?.length > 1) {
      timerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % reviews?.length);
      }, reviewsIntervalMs);
    }
  };

  const featured = reviews?.[activeIndex] ?? null;

  if (loading) {
    return (
      <section style={{ padding: '80px 0', background: '#fff', textAlign: 'center' }}>
        <p style={{ color: '#9ca3af', fontSize: 14 }}>Caricamento recensioni…</p>
      </section>);

  }

  if (!reviews?.length) return null;

  return (
    <section style={{ padding: '80px 0', background: '#fff' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
        {/* Section header */}
        <h2
          style={{
            fontSize: 32,
            fontWeight: 700,
            color: '#013776',
            textAlign: 'center',
            marginBottom: 8,
            lineHeight: 1.2
          }} className="text-[rgba(16,77,152,1)]">

          Cosa dicono di noi
        </h2>
        <p
          style={{
            fontSize: 14,
            color: '#6b7280',
            textAlign: 'center',
            marginBottom: 48
          }}>

          Professionisti e clienti che hanno già scelto Volaptic
        </p>

        {/* Featured quote */}
        {featured &&
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: 40
          }}>

            <span
            style={{
              fontSize: 80,
              color: '#32b3e5',
              fontFamily: 'Georgia, serif',
              lineHeight: 0.6,
              display: 'block',
              marginBottom: 16,
              opacity: 0.7
            }}>

              ❝
            </span>

            <p
            style={{
              fontSize: 'clamp(20px, 3vw, 26px)',
              fontWeight: 500,
              color: '#0a2540',
              maxWidth: 600,
              textAlign: 'center',
              lineHeight: 1.5,
              fontStyle: 'italic',
              margin: '0 auto 20px auto'
            }}>

              {featured?.quote_text}
            </p>

            <StarRow count={featured?.stars ?? 5} size={20} />

            <p style={{ fontWeight: 700, fontSize: 15, color: '#0a2540', margin: '20px 0 0 0' }}>
              {featured?.author_name}
            </p>
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0 0' }}>
              {[featured?.role, featured?.city]?.filter(Boolean)?.join(' · ')}
            </p>

            {/* Dot indicators — thin horizontal bars */}
            {reviews?.length > 1 &&
          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'center' }}>
                {reviews?.map((_, i) =>
            <button
              key={i}
              onClick={() => handleDotClick(i)}
              aria-label={`Recensione ${i + 1}`}
              style={{
                width: 32,
                height: 3,
                borderRadius: 2,
                background: i === activeIndex ? '#104d98' : '#e2e8f0',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'background 0.2s ease'
              }} />

            )}
              </div>
          }
          </div>
        }

        {/* Bottom 3-column grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 20,
            alignItems: 'stretch'
          }}
          className="recensioni-grid">

          {/* LEFT COLUMN: Card 1 (blue) + Card 2 (white) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ReviewCard
              review={reviews?.[1]}
              bg="#104d98"
              textColor="#ffffff"
              starColor="#f59e0b"
              border="none" />

            <ReviewCard
              review={reviews?.[2]}
              bg="#fff"
              textColor="#0a2540"
              starColor="#f59e0b"
              border="1px solid #e2e8f0" />

          </div>

          {/* CENTER COLUMN: video */}
          <div
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              background: '#f1f5f9',
              display: 'flex',
              alignItems: 'stretch',
              alignSelf: 'stretch'
            }}>

            {videoUrl && !videoError ?
            <video
              key={videoUrl}
              src={videoUrl}
              autoPlay
              muted
              loop
              playsInline
              onError={() => setVideoError(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> :


            <p
              style={{
                color: '#94a3b8',
                fontSize: 14,
                fontWeight: 500,
                textAlign: 'center',
                padding: 24,
                margin: 'auto'
              }}>

                Video in arrivo
              </p>
            }
          </div>

          {/* RIGHT COLUMN: Card 3 (white) + Card 4 (light blue) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ReviewCard
              review={reviews?.[3]}
              bg="#fff"
              textColor="#0a2540"
              starColor="#f59e0b"
              border="1px solid #e2e8f0" />

            <ReviewCard
              review={reviews?.[4]}
              bg="#32b3e5"
              textColor="#ffffff"
              starColor="#f59e0b"
              border="none" />

          </div>
        </div>
      </div>
      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .recensioni-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>);

}